/**
 * CR443: DealIdBadge — Single SSOT component for displaying deal UUID.
 * 
 * - Displays deal.id as a monospace badge
 * - Click to copy to clipboard
 * - Red styling if missing or non-UUID
 * - Admin/dev only by default (hidden on public unless VITE_ENABLE_DEBUG_IDS)
 */

import React, { useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface DealIdBadgeProps {
  dealId: string | null | undefined;
  dedupeKey?: string | null;
  className?: string;
  /** If false, badge is hidden (but data attrs still apply at parent level) */
  visible?: boolean;
}

export function DealIdBadge({ dealId, dedupeKey, className, visible = true }: DealIdBadgeProps) {
  const [copied, setCopied] = useState(false);
  
  const isValid = typeof dealId === "string" && UUID_RE.test(dealId);
  const displayId = isValid ? dealId : "missing-id";

  // Warn once for invalid IDs
  React.useEffect(() => {
    if (!isValid && visible) {
      console.warn("[DealIdBadge] invalid id:", dealId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const handleCopy = useCallback(async () => {
    if (!displayId) return;
    try {
      await navigator.clipboard.writeText(displayId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API not available
    }
  }, [displayId]);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono leading-tight",
        "transition-colors cursor-pointer select-all",
        isValid
          ? "bg-muted/60 text-muted-foreground hover:bg-muted"
          : "bg-destructive/10 text-destructive hover:bg-destructive/20",
        className,
      )}
      title={`Click to copy${dedupeKey ? ` | dedupe: ${dedupeKey}` : ""}`}
      aria-label={`Copy deal ID: ${displayId}`}
    >
      <span className="truncate max-w-[180px]">ID: {displayId}</span>
      {copied ? (
        <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
      ) : (
        <Copy className="w-3 h-3 opacity-50 flex-shrink-0" />
      )}
    </button>
  );
}

export default DealIdBadge;
